import Bugsnag from '@bugsnag/js'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { ArcadeButton, FullWidthFlex, pxToRem, spacing, Stack, Text } from '@cash-design-system/react'
import { BlockerAction } from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import { FormBlockerElementUpsellElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { replaceWithCDNImageUrl } from '@cash-web/shared-util-url'
import { WebInteractClick } from '@squareup/customer-data-events-web'

const UpsellContainer = styled(Stack)`
  display: flex;
  flex-direction: column;
  align-items: center;
  border: ${({ theme }) => theme.pxToRem(1)} solid ${({ theme }) => theme.arcadeColors.component.uiDividerBorder};
  border-radius: 0.75rem;
  padding: ${({ theme }) => theme.spacing.l};
`

const UpsellImage = styled.img<{ imageWidth?: number; imageHeight?: number }>`
  width: 100%;
  height: auto;
  max-width: ${({ imageWidth }) => (imageWidth ? pxToRem(imageWidth) : undefined)};
  aspect-ratio: ${({ imageWidth, imageHeight }) => (imageWidth && imageHeight ? imageWidth / imageHeight : undefined)};
`

export const UpsellButton = (action: BlockerAction) => {
  const { text, localized_text, ...type } = action
  const flow = useFlowContext()
  const logCdfEvent = useLogCashWebCdfEvent()

  const handleButtonClick = () => {
    logCdfEvent(
      WebInteractClick({
        componentName: 'UpsellButton',
        componentType: Object.keys(type)[0],
      })
    )
    flow.executeBlockerAction(action)
  }
  return (
    <ArcadeButton data-testid="arcade-button-upsell" size="cta" prominence="subtle" onClick={handleButtonClick}>
      {text}
    </ArcadeButton>
  )
}

export const UpsellElement = ({
  title,
  subtitle,
  image,
  image_height,
  image_width,
  button_action,
}: FormBlockerElementUpsellElement) => {
  const { darkMode } = useTheme()

  const lightUrl = image?.light_url
  const darkUrl = image?.dark_url ?? lightUrl

  const url = replaceWithCDNImageUrl({ url: darkMode ? darkUrl : lightUrl })

  // Manually disabled because we don't support roundups on the web (yet)
  const isRoundupUpsellEnabled = useFeatureFlag('cash-web-account-roundup-upsells')
  const isRoundupUpsell =
    button_action?.internal_navigation_action?.client_route?.url ===
    'https://internal.cash.app/dl/view/investing/round_ups_onboarding'

  if (isRoundupUpsell && !isRoundupUpsellEnabled) {
    return null
  }

  return (
    <UpsellContainer space={spacing.xs}>
      <FullWidthFlex justifyContent="center" alignItems="center">
        {url && (
          <UpsellImage
            src={url}
            alt="" // Unfortunately no alt text is provided by server. Leave "alt" empty to prevent screen readers from reading the URL.
            onError={() => Bugsnag.notify(new Error(`UpsellElement failed to load image ${url}`))}
            imageWidth={image_width}
            imageHeight={image_height}
          />
        )}
      </FullWidthFlex>

      {title && <Text typography="labelMedium">{title}</Text>}
      {subtitle && (
        <Text typography="body" color="textSubtle" textAlign="center" textWrap="pretty">
          {subtitle}
        </Text>
      )}
      {button_action && <UpsellButton {...button_action} />}
    </UpsellContainer>
  )
}
